/**
 * Impact report — the renewal artifact (2026-08-05 ROI pack).
 *
 * One screen that answers "what did TrailerCast actually do for us this
 * period", in numbers a champion pastes into a QBR: reach (trailers,
 * views, watch-hours, stakeholders, speed-to-first-view), time saved
 * (brief counts × stated baselines — methodology rendered under the
 * number, never hidden), outcomes (win rate once deals are marked), and
 * committee threading across open Decision Rooms.
 *
 * HONESTY RULES (mirrors the backend):
 *   - hoursSaved always shows its math via the methodology line.
 *   - Outcomes render an explicit "start marking outcomes" prompt until
 *     tracked > 0 — no fake win rates, ever (same principle as the
 *     2026-07-22 DealStatusPill fake-data removal).
 *
 * Fetches GET /api/reports/impact?days=&scope=. Styling piggybacks the
 * shared .dash-card system (2026-07-26 design audit).
 */

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const PERIODS = [
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: '365', label: '12 months' },
  { id: 'all', label: 'All time' },
];

function StatTile({ label, value, sub }) {
  return (
    <div className="dash-card" style={{ padding: '16px 18px', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ImpactReport({ authToken, authFetch, isCompanyAdmin }) {
  const [days, setDays] = useState('90');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const doFetch = (url, opts) => (authFetch
    ? authFetch(url, opts)
    : fetch(url, { ...opts, headers: { ...(opts && opts.headers), Authorization: `Bearer ${authToken}` } }));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const scope = isCompanyAdmin ? 'team' : 'personal';
    doFetch(`${API_BASE}/api/reports/impact?days=${days}&scope=${scope}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (!cancelled) { setData(j); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, isCompanyAdmin]);

  if (loading) return <p style={{ color: 'var(--room-text-muted, #64748b)' }}>Crunching your impact numbers…</p>;
  if (error) {
    return (
      <div className="dash-card" style={{ padding: 20 }}>
        <p style={{ color: 'var(--room-text-muted, #64748b)', margin: 0 }}>
          Couldn't load the impact report ({error}).{' '}
          <button type="button" onClick={() => setDays((d) => d)} style={{ border: 'none', background: 'none', color: '#6366f1', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>
            Retry
          </button>
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { reach, timeSaved, outcomes, threading } = data;
  const watched = `${reach.totalWatchHours}h`;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>Impact report</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
            {isCompanyAdmin ? 'Whole team' : 'Your activity'} · the numbers to bring to your next review
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={async () => {
            // Blob download (auth header required, so no plain <a href>).
            try {
              const scope = isCompanyAdmin ? 'team' : 'personal';
              const r = await doFetch(`${API_BASE}/api/reports/impact/pdf?days=${days}&scope=${scope}`);
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `TrailerCast_Impact_${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              // eslint-disable-next-line no-alert
              alert('PDF export failed — try again in a moment.');
            }
          }}
          style={{
            padding: '7px 14px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)',
            background: 'var(--room-card, #fff)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            color: '#4f46e5', cursor: 'pointer',
          }}
        >
          Export PDF
        </button>
        <div style={{ display: 'inline-flex', padding: 3, gap: 3, background: 'var(--room-border-soft, #f1f5f9)', borderRadius: 10 }}>
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setDays(p.id)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                background: days === p.id ? 'var(--room-card, #fff)' : 'transparent',
                color: days === p.id ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                boxShadow: days === p.id ? '0 1px 3px rgba(15,23,42,0.1)' : 'none',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Reach */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <StatTile label="Trailers generated" value={reach.trailersGenerated} />
        <StatTile label="Buyer views" value={reach.totalViews} sub={`${reach.completions} watched to the end`} />
        <StatTile label="Buyer watch time" value={watched} sub="time buyers spent with your pitch" />
        <StatTile label="Stakeholders engaged" value={reach.namedStakeholdersEngaged} sub={`${reach.uniqueViewers} unique viewers total`} />
        <StatTile
          label="Share → first view"
          value={reach.medianHoursToFirstView != null ? `${reach.medianHoursToFirstView}h` : '—'}
          sub="median, across shared links"
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* Time saved */}
        <div className="dash-card" style={{ padding: 18, flex: '1 1 280px' }}>
          <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', fontWeight: 600 }}>Rep time saved</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', marginTop: 4 }}>
            ≈ {timeSaved.estimatedHoursSaved}h
          </div>
          <div style={{ fontSize: 12, color: 'var(--room-text-body, #475569)', marginTop: 8, lineHeight: 1.5 }}>
            {timeSaved.dealBriefs} deal briefs · {timeSaved.handoffBriefs} handoff briefs · {timeSaved.qbrBriefs} QBR briefs
          </div>
          <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 8, lineHeight: 1.45 }}>
            {timeSaved.methodology}
          </div>
        </div>

        {/* Outcomes */}
        <div className="dash-card" style={{ padding: 18, flex: '1 1 280px' }}>
          <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', fontWeight: 600 }}>Deal outcomes</div>
          {outcomes.tracked === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--room-text-body, #475569)', marginTop: 10, lineHeight: 1.55 }}>
              No outcomes marked yet. Mark trailers <strong>won</strong> or <strong>lost</strong> from
              the trailer page (or connect your CRM) and this becomes your win-rate
              and revenue-influenced view — the strongest slide in a renewal deck.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', marginTop: 4 }}>
                {outcomes.winRatePct != null ? `${outcomes.winRatePct}% win rate` : '—'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--room-text-body, #475569)', marginTop: 8 }}>
                {outcomes.won} won · {outcomes.lost} lost · {outcomes.pending} pending
              </div>
              {outcomes.wonValueCents > 0 && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
                  ${(outcomes.wonValueCents / 100).toLocaleString()} closed-won value on trailer-backed deals
                </div>
              )}
            </>
          )}
        </div>

        {/* Threading */}
        <div className="dash-card" style={{ padding: 18, flex: '1 1 280px' }}>
          <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', fontWeight: 600 }}>Committee coverage (open rooms)</div>
          {threading.openRooms === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--room-text-body, #475569)', marginTop: 10 }}>
              No open Decision Rooms in this period.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              {[
                ['Multi-threaded (2+ engaged)', threading.multiThreaded, '#16a34a'],
                ['Single-threaded', threading.singleThreaded, '#d97706'],
                ['Silent', threading.silent, '#dc2626'],
              ].map(([label, n, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--room-text-body, #475569)' }}>{label}</div>
                  <div style={{
                    width: `${threading.openRooms ? Math.max(4, (n / threading.openRooms) * 100) : 4}%`,
                    maxWidth: '40%', height: 8, borderRadius: 4, background: color, opacity: 0.75,
                  }} />
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', minWidth: 18, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 6 }}>
                Single-threaded deals are where forecasts die — open those rooms' coverage panels to see who's gone quiet.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
